import React, { useRef, useState } from 'react'

import { Form, Formik } from 'formik'
import { useRouter } from 'next/router'
import * as Yup from 'yup'

import { MaxEditor, getEditorValueAstHtml, isEditorEmpty } from '../MaxEditor'
import MessageBody from './MessageBody'
import { AttachmentList } from '@/components/AttachmentList'
import Attachment from '@/components/Message/Attachment'
import { COMBO_BOX_MAPPER } from '@/components/Message/comboBoxMapper'
import { handleAddDocument } from '@/components/Message/documents'
import { useGetApplicationQuery } from '@/graphql/services/application'
import { useCreateMessageMutation, useLazyGetRepliesQuery } from '@/services/message'
import { IMessage } from '@/types/unlock'

interface ReplyProps extends IMessage {
  isLastIndex?: boolean
}

export default function Reply(props: ReplyProps) {
  const router = useRouter()
  const inputFile = useRef(null)
  const editorRef = React.useRef(null)
  const { parent_message: parentMessage, attachments, from_profile, isLastIndex } = props

  const { data } = useGetApplicationQuery(
    { externalId: router.query.id as string },
    {
      skip: !router.query.id,
    }
  )
  const [createMessage, { isLoading: createMessageLoading }] = useCreateMessageMutation()
  const [getReplies] = useLazyGetRepliesQuery()
  const [isMessage, setIsMessage] = useState<boolean>(false)
  const [documents, setDocuments] = useState([])

  const handleRemoveDocument = (documentKey: number) => {
    setDocuments(documents.filter((_, key) => key != documentKey))
  }

  const onEditorChange = (setFieldValue) => (editorState, editor) => {
    editorState.read(() => {
      if (isEditorEmpty(editor)) {
        return setFieldValue('body', null)
      }
      const html = getEditorValueAstHtml(editor)
      setFieldValue('body', html)
    })
  }

  return (
    <Formik
      validationSchema={Yup.object().shape({
        body: Yup.string().required('Message body is required!'),
        document_type: Yup.string().required('Message type is required!'),
      })}
      initialValues={{
        body: '',
        document_type: 'request-information',
      }}
      onSubmit={async (values, { resetForm, setErrors }) => {
        if (
          values.document_type != 'request-document' &&
          values.document_type != 'request-information' &&
          !documents.length
        ) {
          setErrors({ document_type: 'Attach a document when selecting message type Acknowledge!' })
        } else {
          const formData = new FormData()
          formData.append('body', values.body)
          formData.append('type', COMBO_BOX_MAPPER[values.document_type]['messageType'])
          formData.append('application', String(data.id))
          formData.append('to_profile', String(from_profile?.id))
          formData.append('parent_message', String(parentMessage))

          if (
            values.document_type != 'request-document' &&
            values.document_type != 'request-information' &&
            documents.length
          ) {
            formData.append('kind', COMBO_BOX_MAPPER[values.document_type]['documentKind'])
            formData.append(
              'document',
              new Blob([documents[0]['content']]),
              documents[0]['document']['name']
            )
          }

          await createMessage(formData)
          setDocuments([])
          setIsMessage(false)
          getReplies({
            externalId: router.query.id,
            parentMessageId: parentMessage,
          })
          resetForm()
        }
      }}
    >
      {({ setFieldValue }) => (
        <Form className={`flex w-full gap-2 relative`}>
          <div
            className={`w-full card shadow-md flex flex-col border bg-gray-100 ${
              isLastIndex ? '!rounded-b-2xl rounded-t-none' : 'rounded-none'
            }`}
          >
            <MessageBody
              {...props}
              setIsMessage={setIsMessage}
              isMessage={isMessage}
            />
            <AttachmentList attachments={attachments} />
          </div>
          {isMessage && (
            <div className='px-2'>
              <div className='my-3 flex flex-col gap-3'>
                <MaxEditor
                  editorRef={editorRef}
                  customInitialConfig={{ namespace: 'customer-reply-msg-editor' }}
                  onChange={onEditorChange(setFieldValue)}
                />
                {documents.map((document, dKey) => {
                  return (
                    <Attachment
                      key={dKey}
                      documentKey={dKey}
                      documentName={document.document.name}
                      handleRemoveDocument={handleRemoveDocument}
                    />
                  )
                })}
                <div className='flex flex-row text-xs justify-between items-center mb-2'>
                  <p
                    className='cursor-pointer'
                    onClick={() => setIsMessage(false)}
                  >
                    Close
                  </p>
                  <div className='flex flex-row gap-3 items-center'>
                    <input
                      type='file'
                      id='document'
                      name='document'
                      ref={inputFile}
                      className='hidden'
                      disabled={createMessageLoading}
                      onChange={(file) => handleAddDocument(file, setDocuments, true)}
                    />
                    <button
                      className='bg-[#273a88] rounded text-white
                        w-20 h-7 ease-linear transition-all duration-150
                        hover:shadow-md hover:bg-[#475cb3]'
                      type='submit'
                      disabled={createMessageLoading}
                    >
                      {createMessageLoading ? 'Loading...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Form>
      )}
    </Formik>
  )
}
